import { type UseModalState } from "@clipboard-health/ui-react";
import { Box, Drawer as MuiDrawer, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { useDrag } from "@use-gesture/react";
import { useCallback, useEffect, useRef } from "react";

import { useBottomSheetNesting } from "./useBottomSheetNesting";
import { useModalsContainerContext } from "./useModalsContainerContext";

const BOTTOM_SHEET_TIMING = {
  enter: 420,
  exit: 320,
};

const BOTTOM_SHEET_EASING = {
  enter: "cubic-bezier(0.32, 0.72, 0, 1)",
  exit: "cubic-bezier(0.3, 0, 0.6, 1)",
};

/** Fraction of sheet height dragged down before auto-dismissing */
const DISMISS_THRESHOLD = 0.25;
/** Downward flick velocity (px/ms) that triggers dismiss regardless of distance */
const DISMISS_VELOCITY = 0.4;

/**
 * Logarithmic dampening for overdrag (dragging past the top).
 * Creates a stretchy, rubbery feel that resists further dragging.
 */
function dampenValue(distance: number): number {
  return 8 * Math.log(distance + 1);
}

export interface BottomSheetProps {
  children: React.ReactNode;
  modalState: UseModalState;
  sx?: SxProps<Theme>;
  maxHeightPercent?: number;
  minHeight?: string;
  /** When false, prevents closing via swipe and backdrop click. Defaults to true. */
  dismissible?: boolean;
  /** Fired after the open or close animation completes. Receives the final open state. */
  onAnimationEnd?: (isOpen: boolean) => void;
}

/**
 * BottomSheet component for mobile devices.
 * Appears from the bottom of the screen with a light gray overlay.
 *
 * Close behaviors (when `dismissible` is true):
 * - Clicking the backdrop
 * - X button inside children
 * - Swipe down gesture on the handle pill
 *
 * Nesting is automatic when used inside a `BottomSheetNestingProvider`.
 * When a second sheet opens, the first scales down and shifts up automatically.
 * Dragging the top sheet interpolates the one below in real-time.
 */
export function BottomSheet(props: BottomSheetProps) {
  const {
    children,
    modalState,
    sx,
    maxHeightPercent = 80,
    minHeight,
    dismissible = true,
    onAnimationEnd,
  } = props;

  const { containerRef } = useModalsContainerContext();
  const container = containerRef?.current ?? null;

  const paperRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const onAnimationEndRef = useRef(onAnimationEnd);
  onAnimationEndRef.current = onAnimationEnd;

  const { currentY, setY, notifyDrag, notifyRelease } = useBottomSheetNesting({
    paperRef,
    modalIsOpen: modalState.modalIsOpen,
  });

  // Fade the backdrop opacity in sync with the sheet's drag position.
  const setBackdropOpacity = useCallback((ratio: number, withTransition = true) => {
    const backdrop = backdropRef.current;
    if (!backdrop) {
      return;
    }
    backdrop.style.transition = withTransition
      ? `opacity 380ms cubic-bezier(0.3, 0, 0.6, 1)`
      : "none";
    backdrop.style.opacity = String(ratio);
  }, []);

  // Slide sheet off-screen and fade backdrop, then call closeModal + onAnimationEnd.
  const animatedClose = useCallback(() => {
    const element = paperRef.current;
    if (!element) {
      modalState.closeModal();
      onAnimationEndRef.current?.(false);
      return;
    }
    const sheetHeight = element.offsetHeight;
    setY(sheetHeight + 20);
    setBackdropOpacity(0);
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== element || event.propertyName !== "transform") {
        return;
      }
      element.removeEventListener("transitionend", onTransitionEnd);
      modalState.closeModal();
      onAnimationEndRef.current?.(false);
    };
    element.addEventListener("transitionend", onTransitionEnd);
  }, [setY, setBackdropOpacity, modalState]);

  // Fire onAnimationEnd(true) after the open animation completes
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      return;
    }

    setY(0, false);
    setBackdropOpacity(1, false);

    const element = paperRef.current;
    if (!element) {
      return;
    }

    const onEnterEnd = (event: TransitionEvent) => {
      if (event.target !== element || event.propertyName !== "transform") {
        return;
      }
      element.removeEventListener("transitionend", onEnterEnd);
      onAnimationEndRef.current?.(true);
    };
    element.addEventListener("transitionend", onEnterEnd);

    return () => {
      element.removeEventListener("transitionend", onEnterEnd);
    };
  }, [modalState.modalIsOpen, setY, setBackdropOpacity]);

  const bind = useDrag(
    ({ last, velocity: [, vy], direction: [, dy], offset: [, oy], event }) => {
      // Non-dismissible sheets: allow overdrag with dampening but always snap back
      if (!dismissible) {
        if (oy < 0) {
          const dampened = dampenValue(Math.abs(oy));
          setY(-dampened, false);
        } else if (oy > 0) {
          const dampened = dampenValue(oy);
          setY(dampened, false);
        }

        if (last) {
          setY(0);
        }

        event.preventDefault?.();
        return;
      }

      // Overdrag upward: apply dampening instead of canceling
      if (oy < 0) {
        if (last) {
          setY(0);
          return;
        }
        const dampened = dampenValue(Math.abs(oy));
        setY(-dampened, false);
        event.preventDefault?.();
        return;
      }

      event.preventDefault?.();

      const sheetHeight = paperRef.current?.offsetHeight ?? 400;
      const dismissDistance = sheetHeight * DISMISS_THRESHOLD;
      const percentageDragged = Math.min(1, Math.max(0, oy / sheetHeight));

      if (last) {
        const shouldDismiss = (dy > 0 && vy > DISMISS_VELOCITY) || oy > dismissDistance;

        notifyRelease(!shouldDismiss);

        if (shouldDismiss) {
          animatedClose();
        } else {
          setY(0);
          setBackdropOpacity(1);
        }
      } else {
        setY(oy, false);
        setBackdropOpacity(Math.max(0, 1 - oy / sheetHeight), false);
        notifyDrag(percentageDragged);
      }
    },
    {
      axis: "y",
      filterTaps: true,
      pointer: { touch: true },
      from: () => [0, currentY.current],
    }
  );

  const handleClose = dismissible ? animatedClose : undefined;

  return (
    <MuiDrawer
      anchor="bottom"
      open={modalState.modalIsOpen}
      variant="temporary"
      transitionDuration={BOTTOM_SHEET_TIMING}
      SlideProps={{ easing: BOTTOM_SHEET_EASING }}
      PaperProps={{ ref: paperRef }}
      ModalProps={{
        container: () => container,
        keepMounted: false,
        disableScrollLock: false,
        disableEscapeKeyDown: !dismissible,
        BackdropProps: {
          ref: backdropRef,
          transitionDuration: BOTTOM_SHEET_TIMING,
          sx: {
            transition: `opacity ${BOTTOM_SHEET_TIMING.enter}ms ${BOTTOM_SHEET_EASING.enter}`,
          },
        },
        ...(container && { style: { position: "absolute", zIndex: 1 } }),
      }}
      sx={mergeSxProps(
        (theme: Theme) => ({
          ...(container && { position: "absolute", zIndex: 1 }),
          "& .MuiDrawer-paper": {
            borderTopLeftRadius: theme.borderRadius?.xxLarge,
            borderTopRightRadius: theme.borderRadius?.xxLarge,
            maxHeight: `${maxHeightPercent}vh`,
            minHeight,
            boxShadow: theme.shadow?.bottomSheet,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            willChange: "transform",
            transformOrigin: "center bottom",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            ...(container && { position: "absolute" }),
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            ...(container && { position: "absolute" }),
          },
        }),
        sx
      )}
      onClose={handleClose}
    >
      {/* Drag handle — swipe anywhere on this area to dismiss */}
      <Box
        {...bind()}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingTop: 4,
          paddingBottom: 3,
          flexShrink: 0,
          cursor: dismissible ? "grab" : "default",
          touchAction: "none",
          userSelect: "none",
        }}
      >
        <Box
          sx={(theme) => ({
            width: 54,
            height: 4,
            borderRadius: "100px",
            backgroundColor: theme.palette.divider,
          })}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </MuiDrawer>
  );
}
